import { Link } from "react-router-dom";
import { FileText, ScrollText, Phone, Hospital, AlertTriangle, Scale, DollarSign, UserCheck, Stethoscope, Handshake, Landmark, Wine } from "lucide-react";
import SEO from "../components/SEO";
import CTASection from "../components/sections/CTASection";

const RESOURCES = [
  {
    icon: ScrollText, title: "Indiana Statute of Limitations",
    desc: "2 years for personal injury in most cases. Different rules apply to medical malpractice, dog bites, and government claims.",
    link: "/blog/indiana-statute-of-limitations-personal-injury",
  },
  {
    icon: AlertTriangle, title: "What to Do After an Accident",
    desc: "A step-by-step guide for the first 48 hours after any Indiana accident. The most important checklist you'll read.",
    link: "/blog/what-to-do-after-car-accident-indiana",
  },
  {
    icon: Scale, title: "Indiana Comparative Negligence",
    desc: "How Indiana's 51% rule works — and why insurance companies use it against you.",
    link: "/blog/indiana-comparative-negligence-law",
  },
  {
    icon: Phone, title: "Talking to Insurance",
    desc: "What to say (and not say) to insurance adjusters after an Indiana accident.",
    link: "/blog/should-i-talk-to-insurance-after-accident",
  },
  {
    icon: FileText, title: "Settlement Timelines",
    desc: "How long Indiana accident cases really take and what affects speed.",
    link: "/blog/average-settlement-timeline-indiana",
  },
  {
    icon: Hospital, title: "Uninsured Motorist Guide",
    desc: "What happens when the at-fault driver has no insurance — and how UM coverage protects you.",
    link: "/blog/uninsured-motorist-coverage-indiana",
  },
  {
    icon: DollarSign, title: "How Much Is My Case Worth?",
    desc: "The real factors that decide the value of an Indiana injury claim — medical bills, lost wages, pain and suffering, and coverage limits.",
    link: "/blog/how-much-is-my-indiana-case-worth",
  },
  {
    icon: UserCheck, title: "Do I Need a Lawyer?",
    desc: "When you can handle a claim yourself and when hiring an Indiana attorney meaningfully changes your outcome.",
    link: "/blog/do-i-need-a-lawyer-indiana-accident",
  },
  {
    icon: Stethoscope, title: "Paying Medical Bills",
    desc: "Who pays your medical bills after an Indiana crash, how MedPay and health insurance interact, and protecting yourself from liens.",
    link: "/blog/paying-medical-bills-after-indiana-accident",
  },
  {
    icon: Handshake, title: "Choosing an Attorney",
    desc: "How to vet an Indiana injury lawyer — contingency fees, experience, and the questions to ask before you sign.",
    link: "/blog/how-to-choose-indiana-injury-attorney",
  },
  {
    icon: Landmark, title: "Government Tort Claims",
    desc: "Suing a city, county, or the State of Indiana means strict notice deadlines — 180 or 270 days under IC 34-13-3.",
    link: "/blog/indiana-tort-claim-notice-deadlines",
  },
  {
    icon: Wine, title: "Indiana Dram Shop Law",
    desc: "When a bar or restaurant can be held liable for over-serving a driver who later caused your crash.",
    link: "/blog/indiana-dram-shop-law",
  },
];

export default function Resources() {
  return (
    <>
      <SEO title="Indiana Accident Resources | Free Guides & Information" description="Free Indiana accident resources, guides, and legal information for victims." />
      <section className="pt-12 md:pt-20 pb-12 px-6 bg-bone">
        <div className="max-w-7xl mx-auto">
          <div className="overline mb-3">Resource Hub</div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-forest tracking-tight mb-5 text-balance max-w-3xl">
            Free Indiana accident resources, guides, and answers.
          </h1>
          <p className="text-lg text-charcoal-soft max-w-2xl">
            Everything we wish every Indiana accident victim knew, in one place.
          </p>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {RESOURCES.map((r, i) => (
            <Link key={i} to={r.link} className="card-elevated p-7 group" data-testid={`resource-card-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-terracotta/10 text-terracotta grid place-items-center mb-4">
                <r.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold text-xl text-forest mb-2 leading-tight group-hover:text-terracotta transition-colors">
                {r.title}
              </h3>
              <p className="text-sm text-charcoal-soft leading-relaxed">{r.desc}</p>
            </Link>
          ))}
        </div>
      </section>
      <CTASection />
    </>
  );
}
